<script setup lang="ts">
/**
 * Shared table form modal — create + edit modes.
 * Dark theme with hex-pattern background, skewed pill buttons,
 * whitelist tags, custom checkbox. Matches Django modal CSS.
 */
import { ref, computed, watch } from 'vue'
import { openMyTables } from '@/services/profilePopup'
import { useLobbyStore } from '@/stores/lobby'
import { useAuthStore } from '@/stores/auth'
import type { BoardMeta, TableInfo } from '@/stores/lobby'
import api from '@/api/client'
import UsernameGhostInput from '@/components/common/UsernameGhostInput.vue'

const props = defineProps<{
  visible: boolean
  mode: 'create' | 'edit'
  tableData?: Partial<TableInfo>
  embedded?: boolean
  showBack?: boolean
}>()

const emit = defineEmits<{
  close: []
  created: [tableId: string]
  saved: [tableId: string]
  deleted: [tableId: string]
  open: [tableId: string]
  back: []
}>()

const lobby = useLobbyStore()
const auth = useAuthStore()

const MAX_TABLES_PER_USER = 2

/* Form fields */
const name = ref('')
const boardId = ref('')
const password = ref('')
const gameMode = ref<'1v1' | '2v2'>('1v1')
const visibility = ref<'visible' | 'private'>('visible')
const passwordMode = ref<'open' | 'password'>('open')
const whitelistInput = ref('')
const whitelistTags = ref<string[]>([])
const whitelistLoaded = ref(false)
const whitelistCandidates = ref<Array<{ username: string }>>([])
const allowFriends = ref(true)
const submitting = ref(false)
const error = ref<string | null>(null)
const tableLimitReached = ref(false)
let editLoadVersion = 0

const boards = computed<BoardMeta[]>(() => lobby.serverInfo?.boards ?? [])
const showAdvancedOptions = computed(() => visibility.value === 'private' || passwordMode.value === 'password')
const molochLayoutClass = computed(() => {
  if (passwordMode.value === 'password') return 'moloch-password'
  if (visibility.value === 'private') return 'moloch-private-open'
  return 'moloch-visible-open'
})

const title = computed(() => props.mode === 'create' ? 'Create Table' : 'Edit Table')

function getDefaultTableName(): string {
  const nick = auth.username?.trim() || 'Player'
  return `${nick}'s Table`
}

function onOverlayPointerDown(): void {
  close()
}

function openTable(): void {
  if (!props.tableData?.id) return
  emit('open', props.tableData.id)
}

/* Populate form when editing */
watch(() => props.visible, async (val) => {
  const loadVersion = ++editLoadVersion
  if (val && props.mode === 'edit' && props.tableData) {
    name.value = props.tableData.name ?? ''
    boardId.value = props.tableData.board_id ?? ''
    gameMode.value = props.tableData.game_mode ?? '1v1'
    visibility.value = props.tableData.visibility === 'private' ? 'private' : 'visible'
    passwordMode.value = props.tableData.has_password ? 'password' : 'open'
    allowFriends.value = props.tableData.allow_friends ?? true
    whitelistTags.value = []
    whitelistLoaded.value = false
    try {
      const { data } = await api.get<{ whitelist?: string[] }>(`/tables/${props.tableData.id}`)
      if (loadVersion === editLoadVersion && props.visible) {
        whitelistTags.value = Array.isArray(data.whitelist) ? data.whitelist : []
        whitelistLoaded.value = true
      }
    } catch {
      if (loadVersion === editLoadVersion) {
        error.value = 'Could not load the current table whitelist.'
      }
    }
  } else if (val && props.mode === 'create') {
    resetForm()
    name.value = getDefaultTableName()
    if (boards.value.length > 0 && !boardId.value) {
      boardId.value = boards.value[0].id
    }
  }
  if (val && whitelistCandidates.value.length === 0) {
    try {
      const { data } = await api.get<Array<{ username: string }>>('/social/friends')
      whitelistCandidates.value = Array.isArray(data) ? data : []
    } catch {
      // Whitelist entry remains usable without optional friend suggestions.
    }
  }
}, { immediate: true })

async function handleSubmit(): Promise<void> {
  tableLimitReached.value = false
  if (!name.value.trim()) {
    error.value = 'Table name is required'
    return
  }
  if (!boardId.value) {
    error.value = 'Select a board'
    return
  }

  submitting.value = true
  error.value = null

  try {
    if (props.mode === 'create') {
      const tableId = await lobby.createTable({
        name: name.value.trim(),
        board_id: boardId.value,
        game_mode: gameMode.value,
        visibility: visibility.value === 'private' ? 'private' : 'public',
        password: passwordMode.value === 'password' ? password.value : undefined,
        whitelist: whitelistTags.value.length > 0 ? whitelistTags.value : undefined,
        allow_friends: allowFriends.value,
      })
      emit('created', tableId)
      resetForm()
    } else if (props.tableData?.id) {
      const passwordUpdate = passwordMode.value === 'open'
        ? ''
        : (password.value.trim() ? password.value : undefined)
      await lobby.updateTable(props.tableData.id, {
        name: name.value.trim(),
        board_id: boardId.value,
        game_mode: gameMode.value,
        visibility: visibility.value === 'private' ? 'private' : 'public',
        password: passwordUpdate,
        whitelist: whitelistLoaded.value ? whitelistTags.value : undefined,
        allow_friends: allowFriends.value,
      })
      emit('saved', props.tableData.id)
    }
  } catch (err: any) {
    const responseError = err.response?.data?.error
    tableLimitReached.value = err.response?.data?.code === 'table_limit_reached'
      || /maksymalnie 2|up to 2 tables/i.test(String(responseError || ''))
    if (tableLimitReached.value) {
      close()
      openMyTables(true)
      return
    }
    error.value = responseError || 'An error occurred.'
  } finally {
    submitting.value = false
  }
}

async function handleDelete(): Promise<void> {
  if (!props.tableData?.id) return
  if (!confirm(`Delete table "${name.value}"?`)) return

  submitting.value = true
  try {
    await lobby.deleteTable(props.tableData.id)
    emit('deleted', props.tableData.id)
    resetForm()
  } catch (err: any) {
    error.value = err.response?.data?.error || 'Delete failed.'
  } finally {
    submitting.value = false
  }
}

function addWhitelistTag(suggested?: string): void {
  const tag = String(suggested || whitelistInput.value).trim()
  if (tag && !whitelistTags.value.includes(tag)) {
    whitelistTags.value.push(tag)
  }
  whitelistInput.value = ''
}

function removeWhitelistTag(tag: string): void {
  whitelistTags.value = whitelistTags.value.filter(t => t !== tag)
}

function resetForm(): void {
  name.value = getDefaultTableName()
  boardId.value = boards.value.length > 0 ? boards.value[0].id : ''
  password.value = ''
  gameMode.value = '1v1'
  visibility.value = 'visible'
  passwordMode.value = 'open'
  whitelistInput.value = ''
  whitelistTags.value = []
  whitelistLoaded.value = false
  allowFriends.value = true
  error.value = null
  tableLimitReached.value = false
}

function close(): void {
  resetForm()
  emit('close')
}
</script>

<template>
  <Teleport to="body" :disabled="embedded">
    <div
      v-if="visible"
      :class="embedded ? 'table-form-embedded' : 'modal-overlay active'"
      @pointerdown.self="onOverlayPointerDown"
    >
      <div class="modal-content nhex-popup-surface nhex-popup--table-form" style="min-height: 520px; display: flex; flex-direction: column;">
        <button v-if="showBack" type="button" class="modal-back" aria-label="Back to My Tables" @click="emit('back')">&#8592;</button>
        <button class="modal-close" @click="close">&times;</button>
        <div class="modal-title">{{ title }}</div>

        <form autocomplete="off" @submit.prevent="handleSubmit" style="flex: 1; display: flex; flex-direction: column;">
          <!-- Name + Board row -->
          <div class="modal-row">
            <div class="modal-field">
              <label>Table Name</label>
              <input
                v-model="name"
                type="text"
                name="nhex-table-label"
                autocomplete="off"
                data-1p-ignore
                data-lpignore="true"
                data-form-type="other"
                spellcheck="false"
                maxlength="50"
              />
            </div>
            <div class="modal-field">
              <label>Board</label>
              <select v-model="boardId">
                <option v-for="board in boards" :key="board.id" :value="board.id">
                  {{ board.name }}
                </option>
              </select>
            </div>
          </div>

          <!-- Three pills row -->
          <div class="modal-pills">
            <!-- Game Mode -->
            <div class="modal-pill">
              <button
                type="button"
                class="pill-btn"
                :class="{ active: gameMode === '1v1' }"
                @click="gameMode = '1v1'"
              ><span>1v1</span></button>
              <button
                type="button"
                class="pill-btn"
                :class="{ active: gameMode === '2v2' }"
                @click="gameMode = '2v2'"
              ><span>2v2</span></button>
            </div>
            <!-- Visibility -->
            <div class="modal-pill">
              <button
                type="button"
                class="pill-btn"
                :class="{ active: visibility === 'visible' }"
                @click="visibility = 'visible'"
              ><span>👁 Visible</span></button>
              <button
                type="button"
                class="pill-btn"
                :class="{ active: visibility === 'private' }"
                @click="visibility = 'private'"
              ><span>🕵️ Private</span></button>
            </div>
            <!-- Password -->
            <div class="modal-pill">
              <button
                type="button"
                class="pill-btn"
                :class="{ active: passwordMode === 'open' }"
                @click="passwordMode = 'open'"
              ><span>🌍 Open</span></button>
              <button
                type="button"
                class="pill-btn"
                :class="{ active: passwordMode === 'password' }"
                @click="passwordMode = 'password'"
              ><span>🔒 Password</span></button>
            </div>
          </div>

          <!-- Password field (conditional) -->
          <div v-show="passwordMode === 'password'" class="modal-field active">
            <label>Password</label>
            <input
              v-model="password"
              type="password"
              name="nhex-table-secret"
              autocomplete="new-password"
              data-1p-ignore
              data-lpignore="true"
              data-form-type="other"
              maxlength="50"
              :placeholder="mode === 'edit' && tableData?.has_password ? 'Leave blank to keep current password' : 'Enter password'"
            />
          </div>

          <!-- Whitelist section (visible when private or password) -->
          <div v-if="showAdvancedOptions">
            <div class="modal-whitelist-row">
              <UsernameGhostInput
                v-model="whitelistInput"
                :candidates="whitelistCandidates"
                placeholder="Add player to whitelist..."
                @submit="addWhitelistTag"
              />
              <label class="allow-friends-label">
                <input v-model="allowFriends" type="checkbox" />
                Allow Friends
              </label>
            </div>
            <div v-if="whitelistTags.length > 0" class="whitelist-tags">
              <span
                v-for="tag in whitelistTags"
                :key="tag"
                class="whitelist-tag"
              >
                {{ tag }}
                <span class="remove-tag" @click="removeWhitelistTag(tag)">&times;</span>
              </span>
            </div>
          </div>

          <!-- Error -->
          <p v-if="error" class="modal-error">
            <template v-if="tableLimitReached">
              You can only own up to {{ MAX_TABLES_PER_USER }} tables.
              <button type="button" class="table-limit-link" @click="close(); openMyTables(true)">
                Delete an existing table first
              </button>.
            </template>
            <template v-else>{{ error }}</template>
          </p>

          <!-- Footer buttons -->
          <div class="modal-footer">
            <button
              type="submit"
              class="modal-submit-btn"
              :disabled="submitting"
            >
              {{ mode === 'create' ? 'Create Table' : 'Save Changes' }}
            </button>
            <button
              v-if="mode === 'edit'"
              type="button"
              class="modal-submit-btn"
              :disabled="submitting"
              @click="openTable"
            >
              Open Table
            </button>
            <button
              v-if="mode === 'edit'"
              type="button"
              class="modal-delete-btn"
              style="display: inline-block;"
              :disabled="submitting"
              @click="handleDelete"
            >
              Delete Table
            </button>
          </div>
        </form>

        <!-- Moloch pixel art at bottom -->
        <div class="modal-image" :class="molochLayoutClass">
          <img :src="'/media/site/Moloch.avif'" alt="" draggable="false" />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.table-form-embedded { display:contents; }
.modal-back { position:absolute; z-index:20; top:.5rem; left:.65rem; width:32px; height:32px; padding:0; display:grid; place-items:center; border:0; background:transparent; color:#aeb7c0; font:400 23px/1 Inconsolata,monospace; cursor:pointer; transition:color .14s ease; }
.modal-back:hover { color:#fff; transform:none; }
.modal-field select {
  appearance:none;
  border:1px solid transparent;
  background:rgba(18,24,30,.82);
  color:#edf3f7;
  box-shadow:inset 0 0 0 1px rgba(255,255,255,.08);
  color-scheme:dark;
}
.modal-field select option { background:#1a1a1a; color:#fff; }
</style>

<style scoped>
.modal-image {
  right: auto;
  width: fit-content;
  max-width: fit-content;
  transform-origin: bottom left;
  transition: left 220ms ease, transform 220ms ease;
}

.modal-image.moloch-visible-open {
  left: -31px;
  transform: scale(1.86);
}

.modal-image.moloch-private-open {
  left: 25px;
  transform: scale(1.5);
}

.modal-image.moloch-password {
  left: 90px;
  transform: scale(1);
}

.modal-error {
  color: #e57373;
  font-size: 0.85rem;
  margin-top: 0.5rem;
}

.table-limit-link {
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;
}
</style>
